<script setup>
import notification from "@/utils/plugins/NotificationService";
import { workRecordRequestApiError } from "@/utils/presenceAbsence";
import { Select, CloseBold } from '@element-plus/icons-vue';
import { computed, ref } from 'vue';
import { useUiStore } from "@/app/stores/uiStore";

// Icons
import IslandsIcon from '@/components/icons/IconIslands.vue';
import ChronometerIcon from '@/components/icons/IconChronometer.vue';
import IconServers from '@/components/icons/IconServers.vue';
import IconMagnifyingGlass from '@/components/icons/IconMagnifyingGlass.vue';
import IconHatGraduation from '@/components/icons/IconHatGraduation.vue';
import IconVehicle from '@/components/icons/IconVehicle.vue';
import IconRestart from '@/components/icons/IconRestart.vue';
import IconBlueBox from '@/components/icons/IconBlueBox.vue';
import IconMeetingRoomReservation from '@/components/icons/IconMeetingRoomReservation.vue';
import IconSocialFund from '@/components/icons/IconSocialFund.vue';

// Services
import PresenceService from "@/api/services/newPresencia.service";
import HolidayService from "@/api/services/holiday.service";
import VehicleRequestService from "@/api/services/vehicleRequest.service";
import CourseEnrollmentService from '@/api/services/courseEnrollment.service';
import CourseRequestService from '@/api/services/courseRequest.service';

// My components
import VehicleCancelDialog from "@/components/shared/Vehicles/VehicleCancelDialog.vue";
import VehicleRejectDialog from "@/components/shared/Vehicles/VehicleRejectDialog.vue";
import VehicleReturn from "@/components/shared/Vehicles/VehicleReturn.vue";
import VehicleConfirmDialog from "@/components/shared/Vehicles/VehicleConfirmDialog.vue";
import ConfirmMaterialDialog from "@/components/shared/Material/ConfirmMaterialDialog.vue";
import EmployeeNotificationService from '@/api/services/employeeNotification.service';
import DepartmentNotificationService from '@/api/services/departmentNotification.service';
import priorityColors from "@const/priorityColors";


// eslint-disable-next-line no-undef
const props = defineProps({
    item: {
        category: Object,
        required: true
    },
    showActionsColumn: {
        type: Boolean,
        default: true
    },
    compactPinnedMobile: {
        type: Boolean,
        default: false
    },
});


const uiStore = useUiStore();

const inboxItem = computed(() => {
    if (props && props.item) {
        return props.item;
    }
    return null;
})

const statusLabel = ref({
    approved: "Aprobado",
    rejected: "Rechazado",
    pending: "Pendiente",
    cancelled: "Cancelado",
    confirmed: "Confirmado"
})

const statusClass = ref({
    approved: "btn-green",
    confirmed: "btn-green",
    rejected: "btn-red",
    pending: "btn-yellow",
    cancelled: "btn-red"
})


const dialogRejectRequest = ref(false);
const dialogCancelRequest = ref(false);
const dialogReturnRequest = ref(false);
const dialogConfirmRequest = ref(false);
const dialogConfirmMaterial = ref(false);

const selectedRequest = ref();
const selectedMaterial = ref();

const vehicleReturnRef = ref(null)
const mediumPriorityColor = '#f7d20d';

const priorityName = computed(() => {
    const item = inboxItem.value;
    return item?.priority?.name
        ?? item?.priority
        ?? item?.ticketPriority?.name
        ?? item?.actionMetadata?.priority?.name
        ?? item?.actionMetadata?.priority
        ?? null;
})

const isPinned = computed(() => {
    const item = inboxItem.value;
    return Boolean(item?.pinned ?? item?.isPinned ?? item?.fixed ?? item?.starred ?? item?.favorite);
})

const isArchived = computed(() => {
    const item = inboxItem.value;
    return Boolean(item?.archived ?? item?.isArchived);
})

const normalizePreviewText = (value) => {
    return String(value)
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

const decodeHtmlEntities = (value) => {
    if (typeof document === 'undefined') {
        return value
            .replace(/&nbsp;/gi, ' ')
            .replace(/&amp;/gi, '&')
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/&quot;/gi, '"')
            .replace(/&#39;/gi, "'");
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
}

const formatHtmlPreview = (value) => {
    if (!value) {
        return '';
    }

    const html = String(value);

    if (typeof DOMParser !== 'undefined') {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        doc.querySelectorAll('style, script, template, noscript').forEach(element => element.remove());
        return normalizePreviewText(doc.body?.textContent ?? '');
    }

    const withoutNonVisibleBlocks = html
        .replace(/<!--[\s\S]*?-->/g, ' ')
        .replace(/<(style|script|template|noscript)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ');
    const withoutTags = withoutNonVisibleBlocks.replace(/<[^>]*>/g, ' ');

    return normalizePreviewText(decodeHtmlEntities(withoutTags));
}

const previewText = computed(() => {
    const content = inboxItem.value?.content ?? inboxItem.value?.body ?? inboxItem.value?.message ?? '';
    return formatHtmlPreview(content);
})

const priorityLabel = computed(() => {
    switch (String(priorityName.value ?? '').toLowerCase()) {
        case 'high':
        case 'alta':
            return 'Alta';
        case 'medium':
        case 'media':
            return 'Media';
        case 'low':
        case 'baja':
            return 'Baja';
        default:
            return null;
    }
})

const priorityTooltip = computed(() => {
    return priorityLabel.value ? `Prioridad ${priorityLabel.value}` : 'Sin prioridad';
})

const priorityOptions = [
    { label: 'Alta', value: 'high' },
    { label: 'Media', value: 'medium' },
    { label: 'Baja', value: 'low' },
]

const actionMetadataActions = computed(() => {
    const metadata = inboxItem.value?.actionMetadata;
    if (!metadata) {
        return [];
    }

    const actions = Array.isArray(metadata.actions) ? [...metadata.actions] : [];
    if (typeof metadata.actions === 'string') {
        actions.push(metadata.actions);
    }
    ['action', 'actionType', 'type'].forEach(key => {
        if (typeof metadata[key] === 'string') {
            actions.push(metadata[key]);
        }
    });

    return [...new Set(actions)];
})

const hasActionMetadataActions = computed(() => actionMetadataActions.value.length > 0)


// eslint-disable-next-line no-undef
const emit = defineEmits(['openDialog', 'reloadInbox'])

// Función para retornar el título según tipo
function titleForcategory(category) {
    switch (category) {
        case 'presence': return 'Presencia';
        case 'ticket': return 'Tickets';
        case 'project': return 'Proyectos';
        case 'training': return 'Formación';
        case 'holiday': return 'Vacaciones';
        case 'suggestion': return 'Sugerencias';
        case 'material': return 'Material';
        case 'social_fund': return 'Fondo Social';
        case 'VEHICULOS': return 'Vehículos';
        case 'ALERTAS PROYECTOS': return 'Proyectos';
        case 'SALA REUNIONES': return 'Sala reuniones';
        default: return category ? category : '';
    }
}

function isLongInboxTitle(category) {
    return category === 'SALA REUNIONES' || category === 'meeting_room';
}

function openInboxDialog(item) {
    emit('openDialog', item)
}

const reloadInbox = () => {
    emit('reloadInbox');
}

const hasVehicleAction = (action) => {
    return actionMetadataActions.value.includes(action);
}

const formatDate = (date) => {
    if (!date) {
        return '';
    }

    const newD = new Date(date);
    if (Number.isNaN(newD.getTime())) {
        return '';
    }

    const now = new Date();

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const targetDay = new Date(newD.getFullYear(), newD.getMonth(), newD.getDate());

    function pad2(n) {
        return n.toString().padStart(2, '0');
    }
    const hhmm = `${pad2(newD.getHours())}:${pad2(newD.getMinutes())}`;
    if (targetDay.getTime() === today.getTime()) {
        return `Hoy ${hhmm}`;
    } else if (targetDay.getTime() === yesterday.getTime()) {
        return `Ayer ${hhmm}`;
    } else {
        const dd = pad2(newD.getDate());
        const mm = pad2(newD.getMonth() + 1);
        const yyyy = newD.getFullYear();
        return `${dd}/${mm}/${yyyy} ${hhmm}`;
    }
}

const priorityColor = (priority) => {
    if (!priority) {
        return '#919191';
    }
    if (String(priority).toLowerCase() === 'medium' || String(priority).toLowerCase() === 'media') {
        return mediumPriorityColor;
    }
    const color = priorityColors.getColor(priority);
    return color === 'white' ? '#919191' : color;
}

const priorityIcon = (priority) => {
    if (!priority) {
        return 'fa-thermometer-empty';
    }
    const icon = priorityColors.getIcon(priority);
    return icon === 'white' ? 'fa-thermometer-empty' : icon;
}

const isSuccessfulResponse = (response) => {
    return response?.status >= 200 && response?.status < 300;
}

const ensureSuccessfulResponse = (response) => {
    if (!isSuccessfulResponse(response)) {
        throw new Error('La operación no se ha completado correctamente');
    }
    return response;
}

const changePriority = async (priority) => {
    if (priority === String(priorityName.value ?? '').toLowerCase()) {
        return;
    }

    try {
        uiStore.startLoading();
        const res = await updateNotification({ priority });
        ensureSuccessfulResponse(res);
        notification.addNotification({
            type: 'success',
            message: 'Prioridad actualizada correctamente',
            duration: 6000
        })
        reloadInbox();
    } catch (error) {
        console.error('Error actualizando prioridad:', error);
    } finally {
        uiStore.stopLoading();
    }
}

const updateNotification = (params) => {
    if (!inboxItem.value || !inboxItem.value['@type']) {
        throw new Error("Tipo de notificación no definido")
    }

    if (inboxItem.value['@type'] === "DepartmentNotification") {
        return DepartmentNotificationService.updateDepartmentNotification(inboxItem.value.id, params)
    }
    if (inboxItem.value['@type'] === "EmployeeNotification") {
        return EmployeeNotificationService.updateEmployeeNotification(inboxItem.value.id, params)
    }

    throw new Error(`Tipo no soportado: ${inboxItem.value['@type']}`)
}

const runNotificationEndpoint = (employeeMethod, departmentMethod) => {
    if (!inboxItem.value || !inboxItem.value['@type']) {
        throw new Error("Tipo de notificación no definido")
    }

    if (inboxItem.value['@type'] === "DepartmentNotification") {
        return departmentMethod(inboxItem.value.id)
    }
    if (inboxItem.value['@type'] === "EmployeeNotification") {
        return employeeMethod(inboxItem.value.id)
    }

    throw new Error(`Tipo no soportado: ${inboxItem.value['@type']}`)
}

const togglePinned = async () => {
    try {
        uiStore.startLoading();
        if (isPinned.value) {
            const res = await runNotificationEndpoint(
                EmployeeNotificationService.unpinEmployeeNotification,
                DepartmentNotificationService.unpinDepartmentNotification
            );
            ensureSuccessfulResponse(res);
        } else {
            const res = await runNotificationEndpoint(
                EmployeeNotificationService.pinEmployeeNotification,
                DepartmentNotificationService.pinDepartmentNotification
            );
            ensureSuccessfulResponse(res);
        }
        reloadInbox();
    } catch (error) {
        console.error('Error actualizando fijado:', error);
    } finally {
        uiStore.stopLoading();
    }
}

const toggleRead = async () => {
    try {
        uiStore.startLoading();
        const res = await updateNotification({ emailRead: !inboxItem.value.emailRead });
        ensureSuccessfulResponse(res);
        reloadInbox();
    } catch (error) {
        console.error('Error actualizando lectura:', error);
    } finally {
        uiStore.stopLoading();
    }
}

const toggleArchived = async () => {
    try {
        uiStore.startLoading();
        if (isArchived.value) {
            const res = await runNotificationEndpoint(
                EmployeeNotificationService.unarchiveEmployeeNotification,
                DepartmentNotificationService.unarchiveDepartmentNotification
            );
            ensureSuccessfulResponse(res);
        } else {
            const res = await runNotificationEndpoint(
                EmployeeNotificationService.archiveEmployeeNotification,
                DepartmentNotificationService.archiveDepartmentNotification
            );
            ensureSuccessfulResponse(res);
        }
        notification.addNotification({
            type: 'success',
            message: isArchived.value ? 'Mensaje desarchivado correctamente' : 'Mensaje archivado correctamente',
            duration: 6000
        })
        reloadInbox();
    } catch (error) {
        console.error('Error actualizando archivado:', error);
    } finally {
        uiStore.stopLoading();
    }
}

const cancelButtonClick = () => {
    switch (inboxItem.value.actionMetadata.entityType) {
        case `WorkRecordRequest`:
            rejectWorkRecordRequest();
            break;
        case 'Holiday':
            rejectHoliday();
            break;
        case 'VehicleRequest':
            rejectVehicleRequest();
            break;
        case 'CourseEnrollment':
            rejectCourseEnrollment();
            break;
        case 'CourseRequest':
            rejectCourseRequest();
            break;
        default:
            break;
    }
}
const acceptButtonClick = () => {
    switch (inboxItem.value.actionMetadata.entityType) {
        case `WorkRecordRequest`:
            approveWorkRecordRequest();
            break;
        case 'Holiday':
            approveHoliday();
            break;
        case 'VehicleRequest':
            approveVehicleRequest();
            break;
        case 'CourseEnrollment':
            approveCourseEnrollment();
            break;
        case 'CourseRequest':
            approveCourseRequest();
            break;
        case 'MaterialAssignment':
            confirmMaterialAssignment();
            break;
        default:
            break;
    }
}

const approveHoliday = () =>
    runAction(HolidayService.approveHoliday, 'Se aprobaron correctamente las vacaciones')

const rejectHoliday = () =>
    runAction(HolidayService.rejectHoliday, 'Se rechazaron correctamente las vacaciones')

const approveWorkRecordRequest = () =>
    runBatchResolve('approve', 'Se aprobaron correctamente los cambios de registro')

const rejectWorkRecordRequest = () =>
    runBatchResolve('reject', 'Se rechazaron correctamente los cambios de registro')

const runBatchResolve = async (action, successMessage) => {
    const ids = inboxItem.value.actionMetadata.entityIds ?? [inboxItem.value.actionMetadata.entityId];
    const items = ids.map(id => ({ id, action }));
    try {
        const res = await PresenceService.batchResolveWorkRecordsRequestAdmin({
            notificationId: inboxItem.value.id,
            items,
        });
        if (res?.status === 200) {
            notification.addNotification({ type: 'success', message: successMessage, duration: 6000 });
            reloadInbox();
        }
    } catch (error) {
        const { message } = workRecordRequestApiError(error);
        notification.addNotification({ type: 'error', message, duration: 8000 });
    }
}

const approveCourseEnrollment = () =>
    runAction(CourseEnrollmentService.confirmCourseEnrollment, 'Se aprobó correctamente el cambio de registro')

const rejectCourseEnrollment = () =>
    runAction(CourseEnrollmentService.cancelCourseEnrollment, 'Se rechazó correctamente el cambio de registro')

const approveCourseRequest = () =>
    runAction(CourseRequestService.assignCourseRequest, 'Se aprobó correctamente el cambio de registro')

const rejectCourseRequest = () =>
    runAction(CourseRequestService.discardCourseRequest, 'Se rechazó correctamente el cambio de registro')

// Vehicle Request

const approveVehicleRequest = async () => {
    if (!inboxItem.value?.actionMetadata?.entityId) {
        return notification.addNotification({
            type: 'error',
            message: 'Ha ocurrido un error',
            duration: 6000
        })
    }

    try {
        uiStore.startLoading();
        const res = await VehicleRequestService.approveVehicleRequest(inboxItem.value.actionMetadata.entityId)
        if (res?.status === 200 || res?.status === 201) {
            notification.addNotification({
                type: 'success',
                message: 'Solicitud aprobada',
                duration: 4000
            })
            reloadInbox()
        }
    } catch (err) {
        notification.addNotification({
            type: 'error',
            message: 'Ha ocurrido un error',
            duration: 6000
        })
    } finally {
        uiStore.stopLoading();
    }
}

// Mostrar dialog rechazar
const rejectVehicleRequest = async () => {
    await getVehicleRequest(inboxItem.value.actionMetadata.entityId)
    openDialogRejectRequest();
    // runAction(VehicleRequestService.rejectVehicleRequest, 'Se rechazaron correctamente las vacaciones')
}


// Añadir dialogs
const cancelVehicleRequest = async () => {
    await getVehicleRequest(inboxItem.value.actionMetadata.entityId)
    openDialogCancelRequest();
    // Mostrar dialog cancelar
}
const returnVehicleRequest = async () => {
    await getVehicleRequest(inboxItem.value.actionMetadata.entityId)
    openDialogReturnRequest();
    // Hacer petición de vehicle request
}
const confirmVehicleRequest = async () => {
    await getVehicleRequest(inboxItem.value.actionMetadata.entityId)
    dialogConfirmRequest.value = true;
}

const confirmMaterialAssignment = async () => {
    selectedMaterial.value = {
        id: inboxItem.value.actionMetadata.entityId,
    }
    dialogConfirmMaterial.value = true;
}


const runAction = async (serviceFn, successMessage) => {
    if (!inboxItem.value?.actionMetadata?.data) {
        return notification.addNotification({
            type: 'error',
            message: 'Ha ocurrido un error',
            duration: 6000
        })
    }
    const params = {
        notificationId: inboxItem.value.id
    }
    try {
        const res = await serviceFn(inboxItem.value.actionMetadata.entityId, params)
        if (res?.status === 200 || res?.status === 201) {
            notification.addNotification({
                type: 'success',
                message: successMessage,
                duration: 6000
            })
            reloadInbox()
        }
    } catch (err) {
        notification.addNotification({
            type: 'error',
            message: 'Ha ocurrido un error',
            duration: 6000
        })
    }
}

// eslint-disable-next-line no-unused-vars
const runActionEntityOnly = async (serviceFn, successMessage) => {
    if (!inboxItem.value?.actionMetadata?.entityId) {
        return notification.addNotification({
            type: 'error',
            message: 'Ha ocurrido un error',
            duration: 6000
        })
    }
    try {
        const res = await serviceFn(inboxItem.value.actionMetadata.entityId)
        if (res?.status === 200 || res?.status === 201) {
            notification.addNotification({
                type: 'success',
                message: successMessage,
                duration: 6000
            })
            reloadInbox()
        }
    } catch (err) {
        notification.addNotification({
            type: 'error',
            message: 'Ha ocurrido un error',
            duration: 6000
        })
    }
}


// Dialogs vehicle

const openDialogRejectRequest = () => {
    dialogRejectRequest.value = true;
}

const closeDialogRejectVehicleRequest = () => {
    dialogRejectRequest.value = false;
    selectedRequest.value = null;
}

const openDialogReturnRequest = () => {
    dialogReturnRequest.value = true;
}

const closeDialogReturnVehicleRequest = () => {
    dialogReturnRequest.value = false;
    selectedRequest.value = null;
}

const closeDialogConfirmVehicleRequest = () => {
    dialogConfirmRequest.value = false;
    selectedRequest.value = null;
}

const closeDialogConfirmMaterial = (value = false) => {
    dialogConfirmMaterial.value = false;
    selectedRequest.value = null;
    if (value) {
        reloadInbox();
    }
}

const cancelDialogReturnVehicleRequest = () => {
    if (vehicleReturnRef.value) {
        vehicleReturnRef.value.removeAllTickets();
    }
    closeDialogReturnVehicleRequest();
}


const openDialogCancelRequest = () => {
    dialogCancelRequest.value = true;
}
const closeDialogCancelVehicleRequest = () => {
    dialogCancelRequest.value = false;
    selectedRequest.value = null;
}


const getVehicleRequest = async (id) => {
    uiStore.startLoading();
    await VehicleRequestService.getVehicleRequest(id)
        .then((res) => {
            selectedRequest.value = res.data;
            uiStore.stopLoading();
        })
        .catch(() => {
            notification.addNotification({ type: 'error', message: 'Error al cargar los vehiculos.', duration: 6000 });
            uiStore.stopLoading();
        })
}

</script>

<template>
    <div class="home-cards-container">
        <el-row>
            <el-card class="box-card inbox-row-card"
                :class="{ 'inbox-row-card--read': inboxItem.emailRead, 'inbox-row-card--pinned': isPinned }"
                body-style="padding:0;">
                <div class="inbox-row"
                    :class="{ 'inbox-row--without-actions': !showActionsColumn, 'inbox-row--compact-pinned-mobile': compactPinnedMobile }"
                    @click="openInboxDialog(inboxItem)">
                    <div class="inbox-read-cell">
                        <font-awesome-icon v-if="isPinned" icon="thumbtack" class="inbox-pin-indicator" />
                        <span v-else-if="!inboxItem.emailRead" class="inbox-read-dot" />
                    </div>
                    <div class="inbox-category-cell">
                        <div class="card-icon d-flex justify-content-start align-items-center">
                            <IslandsIcon v-if="inboxItem.category === 'VACACIONES'"
                                :class="inboxItem.emailRead ? 'disabled' : ''" />
                            <ChronometerIcon v-if="inboxItem.category === 'PRESENCIA'"
                                :class="inboxItem.emailRead ? 'disabled' : ''" />
                            <IconServers
                                v-if="inboxItem.category === 'TICKET' || inboxItem.category === 'TICKET EXTERNO' || inboxItem.category === 'TICKET INTERNO'"
                                :class="inboxItem.emailRead ? 'disabled' : ''" />
                            <IconMagnifyingGlass
                                v-if="inboxItem.category === 'PROYECTOS' || inboxItem.category === 'ALERTAS PROYECTOS' || inboxItem.category === 'ALERTAS PROYECTOS'"
                                :class="inboxItem.emailRead ? 'disabled' : ''" />
                            <IconHatGraduation
                                v-if="inboxItem.category === 'FORMACION' || inboxItem.category === 'FORMACIÓN' || inboxItem.category === 'training'"
                                :class="inboxItem.emailRead ? 'disabled' : ''" />
                            <IconMagnifyingGlass v-if="inboxItem.category === 'SUGERENCIA'"
                                :class="inboxItem.emailRead ? 'disabled' : ''" />
                            <IconVehicle v-if="inboxItem.category === 'VEHICULOS'"
                                :class="inboxItem.emailRead ? 'disabled' : ''" />
                            <IconMeetingRoomReservation v-if="inboxItem.category === 'SALA REUNIONES'"
                                :class="inboxItem.emailRead ? 'disabled' : ''" />
                            <IconBlueBox v-if="inboxItem.category === 'material'"
                                :class="inboxItem.emailRead ? 'disabled' : ''" />
                            <IconSocialFund v-if="inboxItem.category === 'social_fund'"
                                :class="inboxItem.emailRead ? 'disabled' : ''" />
                            <span class="card-title"
                                :class="[inboxItem.emailRead ? 'disabled' : '', { 'card-title-long': isLongInboxTitle(inboxItem.category) }]">{{
                                    titleForcategory(inboxItem.category) }}</span>
                        </div>
                    </div>
                    <div class="timing inbox-date-cell d-flex justify-content-center align-items-center"
                        :class="inboxItem.emailRead ? 'disabled' : ''">
                        <span>{{ formatDate(inboxItem.sentAt) }}</span>
                    </div>
                    <div class="card-description inbox-message-cell" :class="inboxItem.emailRead ? 'disabled' : ''">
                        <div class="inbox-subject">{{ inboxItem.subject }}</div>
                        <div class="inbox-preview" v-if="previewText">{{ previewText }}</div>
                    </div>
                    <div class="inbox-priority-cell" @click.stop>
                        <el-tooltip placement="top" effect="light" :content="priorityTooltip">
                            <el-dropdown trigger="click" @command="changePriority">
                                <button class="inbox-priority-button" type="button" @click.stop>
                                    <font-awesome-icon class="inbox-priority-icon" :icon="priorityIcon(priorityName)"
                                        :style="{ color: priorityColor(priorityName) }" focusable="false"
                                        aria-hidden="true" />
                                </button>
                                <template #dropdown>
                                    <el-dropdown-menu>
                                        <el-dropdown-item v-for="option in priorityOptions" :key="option.value"
                                            :command="option.value"
                                            :class="{ 'is-active': option.value === String(priorityName ?? '').toLowerCase() }">
                                            <span class="inbox-priority-option"
                                                :style="{ color: priorityColor(option.value) }">
                                                <font-awesome-icon :icon="priorityIcon(option.value)" focusable="false"
                                                    aria-hidden="true" />
                                                {{ option.label }}
                                            </span>
                                        </el-dropdown-item>
                                    </el-dropdown-menu>
                                </template>
                            </el-dropdown>
                        </el-tooltip>
                    </div>
                    <!-- Botones -->
                    <div v-if="showActionsColumn && inboxItem.category != 'VEHICULOS' && inboxItem.actionMetadata && hasActionMetadataActions && !inboxItem.actionTaken"
                        class="inbox-actions-cell d-flex justify-content-end btn-container" @click.stop>
                        <el-button class="btn btn_red align-self-center justify-content-end" :icon="CloseBold" circle
                            v-if="hasVehicleAction('reject') || hasVehicleAction('cancel')" title="Rechazar"
                            @click.stop="cancelButtonClick" />
                        <el-button class="btn btn_darkblue align-self-center justify-content-end" :icon="Select" circle
                            :title="hasVehicleAction('confirm') ? 'Confirmar' : 'Aceptar'"
                            v-if="hasVehicleAction('approve') || hasVehicleAction('confirm')" style="color: #fff;"
                            @click.stop="acceptButtonClick" />
                    </div>
                    <!-- Botones Vehículos -->
                    <div v-else-if="showActionsColumn && inboxItem.category === 'VEHICULOS' && inboxItem.actionMetadata && hasActionMetadataActions && !inboxItem.actionTaken"
                        class="inbox-actions-cell d-flex justify-content-end btn-container" @click.stop>
                        <!-- Boton Rechazar vehículo -->
                        <el-button class="btn btn_red align-self-center justify-content-end" :icon="CloseBold" circle
                            v-if="hasVehicleAction('reject')" title="Rechazar solicitud"
                            @click.stop="rejectVehicleRequest" />
                        <!-- Boton Aprobar vehículo -->
                        <el-button class="btn btn_darkblue align-self-center justify-content-end" :icon="Select" circle
                            title="Aprobar solicitud" v-if="hasVehicleAction('approve')" style="color: #fff;"
                            @click.stop="approveVehicleRequest" />
                        <!-- Boton devolver vehículo -->
                        <el-button class="btn btn_red align-self-center justify-content-end btn-return"
                            :icon="IconRestart" circle title="Devolver solicitud" v-if="hasVehicleAction('cancel')"
                            style="color: #fff;" @click.stop="returnVehicleRequest" />
                        <!-- Boton cancelar vehículo -->
                        <el-button class="btn btn_darkred align-self-center justify-content-end" :icon="CloseBold"
                            circle title="Cancelar solicitud" v-if="hasVehicleAction('return')" style="color: #fff;"
                            @click.stop="cancelVehicleRequest" />
                        <!-- Boton confirmar vehículo -->
                        <el-button class="btn align-self-center justify-content-end" :icon="Select" circle
                            title="Confirmar recepción"
                            v-if="hasVehicleAction('confirm') || hasVehicleAction('confirm_vehicle_return')"
                            style="color: #fff; background-color: #28a745;" @click.stop="confirmVehicleRequest" />
                    </div>
                    <!-- Estado y ver mensaje -->
                    <div v-else-if="showActionsColumn"
                        class="inbox-actions-cell d-flex justify-content-end btn-container" @click.stop>
                        <div v-if="inboxItem.actionTaken" class="d-flex align-items-center">
                            <el-button class="btn status-btn cursor" :class="statusClass[inboxItem.actionTaken]">
                                {{ statusLabel[inboxItem.actionTaken] }}
                            </el-button>
                        </div>
                    </div>
                    <div class="inbox-star-cell" @click.stop>
                        <el-tooltip placement="top" effect="light"
                            :content="isPinned ? 'Quitar de fijados' : 'Fijar mensaje'">
                            <button class="inbox-icon-button" type="button" :class="{ active: isPinned }"
                                @click.stop="togglePinned">
                                <font-awesome-icon icon="star" />
                            </button>
                        </el-tooltip>
                    </div>
                    <div class="inbox-menu-cell" @click.stop>
                        <el-dropdown trigger="click">
                            <button class="inbox-icon-button" type="button" @click.stop>
                                <font-awesome-icon icon="ellipsis-v" />
                            </button>
                            <template #dropdown>
                                <el-dropdown-menu>
                                    <el-dropdown-item @click="toggleRead">
                                        {{ inboxItem.emailRead ? 'Marcar como no leído' : 'Marcar como leído' }}
                                    </el-dropdown-item>
                                    <el-dropdown-item @click="toggleArchived">
                                        {{ isArchived ? 'Desarchivar' : 'Archivar' }}
                                    </el-dropdown-item>
                                </el-dropdown-menu>
                            </template>
                        </el-dropdown>
                    </div>
                </div>
            </el-card>
        </el-row>
        <!-- Modal Cancel Request -->
        <el-dialog :model-value="dialogCancelRequest" :center="true" :lock-scroll="true" :show-close="false"
            :width="750" @close="closeDialogCancelVehicleRequest()" style="border-radius: 20px;">
            <VehicleCancelDialog @close="closeDialogCancelVehicleRequest()" @finish="reloadInbox()"
                :vehicleRequest="selectedRequest" v-if="dialogCancelRequest" />
        </el-dialog>
        <!-- Modal Reject Request -->
        <el-dialog :model-value="dialogRejectRequest" :center="true" :lock-scroll="true" :show-close="false"
            :width="750" @close="closeDialogRejectVehicleRequest()" style="border-radius: 20px;">
            <VehicleRejectDialog @close="closeDialogRejectVehicleRequest()" @finish="reloadInbox()"
                :vehicleRequest="selectedRequest" v-if="dialogRejectRequest" />
        </el-dialog>
        <!-- Modal Return-->
        <el-dialog :model-value="dialogReturnRequest" :center="true" :lock-scroll="true" :show-close="false"
            :width="750" @close="cancelDialogReturnVehicleRequest()" style="border-radius: 20px;">
            <VehicleReturn @close="closeDialogReturnVehicleRequest()" @finish="reloadInbox()" ref="vehicleReturnRef"
                :vehicleRequest="selectedRequest" v-if="dialogReturnRequest" />
        </el-dialog>
        <!-- Modal Confirm Request -->
        <el-dialog :model-value="dialogConfirmRequest" :center="true" :lock-scroll="true" :show-close="false"
            :width="750" @close="closeDialogConfirmVehicleRequest()" style="border-radius: 20px;">
            <VehicleConfirmDialog @close="closeDialogConfirmVehicleRequest()" @finish="reloadInbox()"
                :vehicleRequest="selectedRequest" v-if="dialogConfirmRequest" />
        </el-dialog>
        <!-- Modal Confirm Material -->
        <el-dialog v-model="dialogConfirmMaterial" width="601" :show-close="false" :lock-scroll="true"
            @close="closeDialogConfirmMaterial">
            <!-- Confirmar material -->
            <ConfirmMaterialDialog v-if="dialogConfirmMaterial && selectedMaterial" :material="selectedMaterial"
                @close-dialog="closeDialogConfirmMaterial" />
        </el-dialog>
    </div>
</template>

<style lang="scss" scoped>
#body-content {
    span#title {
        color: $font-blue-color;
        letter-spacing: -0.05em;
        font-size: 2.99em;
        font-weight: bolder;
    }

    #bell {
        fill: $bg-blue;
        width: 45px;
        margin-right: 10px;
        margin-bottom: 30px;
    }

    .timing {
        border-right: 1px solid #00000033;
        border-left: 1px solid #00000033;

        span {
            font-weight: 600;
            font-size: 15px;
        }
    }

    .box-card {
        border-radius: 12px;
        color: $font-blue-color;
        margin-bottom: 15px;

        svg {
            width: 40px;
            fill: $bg-blue;

            &.disabled {
                fill: #919191
            }
        }

        .disabled {
            color: #919191 !important
        }
    }

    .el-card.card-content {
        text-align: center;
    }

    .el-card__header {
        border-bottom: none;
        text-align: left;
        padding: 0px;
    }

    .el-card__body {
        .content-card {
            display: flex;
            padding: 0px;
            color: black;
        }

        .time {
            font-size: 12px;
            color: #999;
        }

        .card-text {
            font-size: 1.0em;
            color: black;
            font-weight: 503;
            max-width: 75%;
        }

        /* & div {
      justify-content: center;
      align-items: flex-end;
    } */
    }

    .btn-icon-plus {
        position: absolute;
        right: 10px;
        bottom: 10px;
        //width: 20px;
        //height: 20px;
        color: #f7d20e;
    }

    .home-cards-container {
        width: 100%;

        .header {
            margin-right: 20px;
        }

        .card-icon {
            min-width: 220px;
            width: 20%;
            height: 100%;

            span {
                letter-spacing: -0.05em;
                font-size: 1.3em;
                font-weight: bolder;
            }

            .card-title {
                margin: 0 5px;
                letter-spacing: -0.05em;
                font-size: 1.5em;
                text-transform: uppercase;
            }
        }

        .card-description {
            color: black;
            font-size: 16px;
            font-weight: 500;
        }

        .btn-container {
            margin-left: -20px;
            padding-right: 0px;
        }
    }
}


.btn-pause-holiday {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    font-size: 13px;
    font-weight: 900;
    line-height: 0.8;
    letter-spacing: 1px;
    // padding: 0;
}

.btn-plus-holiday {
    width: 32px;
    min-width: 32px;
    height: 32px;
    border-radius: 50%;
    font-size: 20px;
    font-weight: 700;
    line-height: 1;
    padding: 0;
    margin-right: 0.7rem;
}

.status-btn {
    padding: 5px 12px;
    border-radius: 6px;
    font-weight: bold;
    border: none;
    width: 112px;
    min-width: 112px;
    height: 32px;
    text-align: center;
    cursor: default;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    /* Verde */
    &.btn-green {
        background-color: #4CAF50;
        color: white;
    }

    /* Rojo */
    &.btn-red {
        background-color: #E53935;
        color: white;
    }

    /* Amarillo */
    &.btn-yellow {
        background-color: #FFC107;
        color: black;
    }
}

.btn-return {
    :deep(.el-icon) {
        width: 1.1em !important;
        height: 1.1em !important;
        font-size: 1em !important;
    }
}

.btn_darkred {
    background: #E53935;
}

.inbox-row-card {
    border-radius: 10px !important;
    margin-bottom: 0 !important;
    border: 1px solid #e5e7f5;
    box-shadow: 0 2px 8px rgba(71, 76, 230, 0.08);
}

.inbox-row-card--read {
    background: #f7f7fa;
    border-color: #ededf2;
}

.inbox-row-card--pinned {
    border-color: #f7d20d;
    background: #fffdf4;
}

.inbox-row-card--read.inbox-row-card--pinned {
    background: #f8f6ee;
}

.inbox-row {
    display: grid;
    grid-template-columns: 38px minmax(150px, 200px) minmax(108px, 138px) minmax(220px, 1fr) 58px 132px 44px 34px;
    align-items: center;
    column-gap: 12px;
    min-height: 48px;
    padding: 8px 12px;
    cursor: pointer;
}

.inbox-row--without-actions {
    grid-template-columns: 38px minmax(150px, 200px) minmax(108px, 138px) minmax(220px, 1fr) 58px 44px 34px;
}

.inbox-read-cell,
.inbox-priority-cell,
.inbox-star-cell,
.inbox-menu-cell {
    display: flex;
    align-items: center;
    justify-content: center;
}

.inbox-read-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: $bg-blue;
}

.inbox-pin-indicator {
    color: #222;
    font-size: 14px;
}

.inbox-category-cell {
    min-width: 0;
}

.inbox-category-cell .card-icon {
    min-width: 0 !important;
    width: auto !important;
    overflow: visible;
}

.inbox-category-cell svg {
    flex: 0 0 auto;
}

.inbox-category-cell .card-title {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.15;
    padding: 0 6px 1px 0;
}

.inbox-date-cell {
    min-width: 0;
    border-left: none !important;
    border-right: none !important;
    justify-content: flex-start !important;
}

.inbox-message-cell {
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.inbox-subject {
    color: #222;
    font-size: 15px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.inbox-preview {
    color: #707080;
    font-size: 13px;
    font-weight: 400;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.inbox-row-card--read .inbox-subject,
.inbox-row-card--read .inbox-preview,
.inbox-row-card--read .timing span {
    color: #919191 !important;
}

.inbox-priority-icon {
    font-size: 22px;
}

.inbox-priority-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    padding: 4px;
    line-height: 1;
}

.inbox-priority-option {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
}

.inbox-priority-cell :deep(.el-dropdown) {
    line-height: 1;
}

.inbox-priority-cell :deep(.el-dropdown-menu__item.is-active) {
    background: #f3f4ff;
}

.inbox-actions-cell {
    gap: 6px;
    min-width: 0;
    margin-left: 0 !important;
    padding-right: 0 !important;
    width: 132px;
    min-width: 132px;
    min-height: 36px;
    align-items: center;
    justify-content: center !important;
}

.inbox-actions-cell .el-button {
    margin-left: 0 !important;
    flex-shrink: 0;
}

.inbox-actions-cell>div {
    width: 100%;
    justify-content: center;
}

.inbox-icon-button {
    border: 0;
    background: transparent;
    color: #919191;
    padding: 4px;
    line-height: 1;
}

.inbox-icon-button.active {
    color: #f7d20d;
}

.inbox-icon-button:hover {
    color: $bg-blue;
}

.inbox-menu-cell :deep(.el-dropdown) {
    line-height: 1;
}

@media (min-width:1131px) and (max-width: 1650px) {
    #body-content {
        .home-cards-container {
            .card-icon {
                .card-title.card-title-long {
                    font-size: 1.2em;
                    line-height: 1.05;
                }
            }
        }
    }
}

@media (max-width: 800PX) {
    #body-content {
        .home-cards-container {
            .card-title {
                font-size: 1.2em !important;
            }
        }
    }

    .card-title {
        font-size: 1.3em !important;
    }

    .card-description {
        font-size: 1em !important;
    }

    .timing {
        span {
            font-size: 12px !important;
        }
    }
}

@media (max-height: 790px),
(max-width: 860px) {
    #body-content {
        .box-card {
            svg {
                width: 25px;
            }
        }
    }
}

@media (max-width:1130px) {
    #body-content {
        .btn-container {
            margin-left: 0px !important;
        }
    }

    .inbox-row {
        grid-template-columns: 32px minmax(140px, 180px) minmax(92px, 120px) minmax(180px, 1fr) 48px 122px 38px 30px;
        column-gap: 8px;
    }

    .inbox-row--without-actions {
        grid-template-columns: 32px minmax(140px, 180px) minmax(92px, 120px) minmax(180px, 1fr) 48px 38px 30px;
    }

    .timing {
        justify-content: flex-start !important;
        margin-top: 0;
        margin-bottom: 0;
        border: none !important;
    }

    .header {
        width: 100% !important;
    }

    .btn-container {
        margin-top: 0;
    }
}

@media (max-width: 900px) {
    .inbox-row {
        grid-template-columns: 30px minmax(126px, 160px) minmax(82px, 108px) minmax(160px, 1fr) 42px 104px 34px 28px;
        column-gap: 6px;
        padding: 8px;
    }

    .inbox-row--without-actions {
        grid-template-columns: 30px minmax(126px, 160px) minmax(82px, 108px) minmax(160px, 1fr) 42px 34px 28px;
    }

    .inbox-category-cell .card-title {
        font-size: 1em !important;
    }

    .inbox-subject {
        font-size: 14px;
    }

    .inbox-preview {
        display: none;
    }
}

@media (max-width: 767px) {
    .home-cards-container {
        margin-bottom: 10px;
    }

    .inbox-row-card {
        border-radius: 12px !important;
        box-shadow: 0 2px 8px rgba(71, 76, 230, 0.08);
    }

    .inbox-row {
        grid-template-columns: 18px minmax(0, 1fr) auto auto auto auto;
        grid-template-areas:
            "read category priority star menu menu"
            ". date actions actions actions actions"
            "message message message message message message";
        column-gap: 10px;
        padding: 18px 16px;
        row-gap: 10px;
        align-items: center;
    }

    .inbox-row:not(.inbox-row--compact-pinned-mobile) {
        grid-template-columns: 18px minmax(0, 1fr) 22px 20px 20px;
        grid-template-areas:
            "read category priority star menu"
            ". date actions actions actions"
            "message message message message message";
        column-gap: 7px;
    }

    .inbox-row--without-actions {
        grid-template-areas:
            "read category priority star menu menu"
            ". date date date date date"
            "message message message message message message";
    }

    .inbox-row--without-actions:not(.inbox-row--compact-pinned-mobile) {
        grid-template-areas:
            "read category priority star menu"
            ". date date date date"
            "message message message message message";
    }

    .inbox-row--compact-pinned-mobile {
        grid-template-columns: 18px 40px minmax(0, 1fr) auto auto auto;
        grid-template-areas:
            "read category . priority star menu"
            ". date date actions actions actions"
            "message message message message message message";
    }

    .inbox-read-cell {
        grid-area: read;
        align-self: center;
    }

    .inbox-category-cell {
        grid-area: category;
    }

    .inbox-category-cell .card-icon {
        gap: 6px;
    }

    .inbox-category-cell svg {
        width: 34px !important;
    }

    .inbox-category-cell .card-title {
        font-size: 1.2em !important;
        line-height: 1.1;
    }

    .inbox-date-cell {
        grid-area: date;
        padding-left: 0;
        justify-content: flex-start !important;
    }

    .inbox-row--compact-pinned-mobile .inbox-category-cell {
        min-width: 34px;
        max-width: 40px;
    }

    .inbox-row--compact-pinned-mobile .inbox-date-cell {
        white-space: nowrap;
        min-width: max-content;
        overflow: visible;
        text-overflow: clip;
    }

    .inbox-row--compact-pinned-mobile .inbox-actions-cell {
        flex-wrap: nowrap;
        justify-content: flex-end !important;
    }

    .inbox-row--compact-pinned-mobile .inbox-category-cell .card-title {
        display: none;
    }

    .inbox-message-cell {
        grid-area: message;
    }

    .inbox-priority-cell {
        grid-area: priority;
    }

    .inbox-priority-icon {
        font-size: 18px;
    }

    .inbox-actions-cell {
        grid-area: actions;
        width: auto;
        min-width: 0;
        min-height: 38px;
        justify-content: flex-end !important;
        gap: 10px;
    }

    .inbox-actions-cell .status-btn {
        width: auto;
        min-width: 92px;
        height: 30px;
        padding: 0 12px;
    }

    .inbox-star-cell {
        grid-area: star;
    }

    .inbox-menu-cell {
        grid-area: menu;
    }

    .inbox-icon-button {
        padding: 2px;
        font-size: 18px;
    }

    .inbox-row:not(.inbox-row--compact-pinned-mobile) .inbox-priority-button,
    .inbox-row:not(.inbox-row--compact-pinned-mobile) .inbox-icon-button {
        width: 20px;
        min-width: 20px;
        padding: 1px;
    }

    .inbox-message-cell {
        gap: 4px;
    }

    .inbox-preview {
        display: block;
        white-space: nowrap;
        word-break: normal;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .inbox-subject {
        white-space: nowrap;
        word-break: normal;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
</style>
